import { Card } from '@employ-engage/common-components';
import { formatMeasure } from '@employ-engage/data';
import { useTranslation } from 'react-i18next';

/**
 * Every scored question, weakest first, with its uncertainty drawn.
 *
 * This panel is what the product offers instead of key-driver analysis, and
 * the difference matters enough to be worth stating: nothing here is a model.
 * Each row is a proportion of the people who answered that question, and the
 * order is just those proportions sorted. No item causes another, none
 * predicts an outcome, and the survey contains no outcome to predict.
 *
 * Which is why the bars are drawn with their intervals. At these sample sizes
 * neighbouring items are usually inside each other's range, and a reader who
 * can see the overlap will not act on a one-place difference. A caption saying
 * "do not over-read the order" does not work; a picture of the overlap does.
 *
 * Vocabulary rules, enforced by the spec beside this file: never "driver",
 * "impact", "leads to", "causes", "influence", or "significant". Permitted:
 * "lowest", "weakest", "move together", "associated with".
 */

export type RankedItem = {
  questionKey: string;
  questionLabel: string;
  value: number | null;
  interval?: { low: number; high: number } | null;
  n: number;
  treatment: string;
  notApplicable: number;
};

export interface ItemRankingPanelProps {
  items?: RankedItem[] | null;
  measure?: string;
  respondentsInScope?: number;
  unscoredQuestions?: number;
  loading?: boolean;
  error?: string | null;
  className?: string;
}


export function ItemRankingPanel({
  items,
  measure = 'topBox',
  respondentsInScope = 0,
  unscoredQuestions = 0,
  loading,
  error,
  className,
}: ItemRankingPanelProps) {
  const { t } = useTranslation();

  const format = (value: number) => formatMeasure(value, measure);

  // A mean sits on the question's own scale; a proportion is always 0-100.
  // Bars are drawn against the largest value present so a five-point scale and
  // a percentage both fill the track sensibly.
  const ceiling = Math.max(
    1,
    ...(items ?? []).map((item) =>
      Math.max(item.value ?? 0, item.interval?.high ?? 0)
    )
  );
  const pct = (value: number) => `${(value / ceiling) * 100}%`;

  const body = () => {
    if (loading) {
      return (
        <p className="text-sm text-light-secondaryText dark:text-dark-secondaryText">
          {t('Working out where the lowest scores are…')}
        </p>
      );
    }
    if (error) return <p className="text-sm text-light-error">{error}</p>;

    if (!items?.length) {
      return (
        <p className="text-sm text-light-secondaryText dark:text-dark-secondaryText">
          {t(
            'Nothing to rank yet. Score a question’s answers and it will ' +
              'appear here.'
          )}
        </p>
      );
    }

    return (
      <ol className="flex flex-col gap-3">
        {items.map((item) => (
          <li key={item.questionKey}>
            <div className="flex flex-wrap items-baseline justify-between gap-x-2">
              <span className="text-sm text-light-primaryText dark:text-dark-primaryText">
                {item.questionLabel}
              </span>
              <span className="text-sm font-medium text-light-primaryText dark:text-dark-primaryText">
                {item.value === null
                  ? t('no answers')
                  : item.treatment === 'count-only'
                  ? // Below the floor a percentage is false precision: one
                    // person moves it more than any real change would.
                    t('{{count}} of {{total}}', {
                      count: Math.round((item.value / 100) * item.n),
                      total: item.n,
                    })
                  : format(item.value)}
              </span>
            </div>

            {item.value !== null && (
              <div
                className="relative mt-1 h-3 w-full rounded bg-light-alternate dark:bg-dark-alternate"
                role="presentation"
              >
                <div
                  className="absolute inset-y-0 rounded bg-light-primary"
                  style={{ width: pct(item.value) }}
                />
                {/* The interval, drawn over the bar. Where two rows' whiskers
                    overlap, their order is not something to act on. */}
                {item.interval && (
                  <div
                    className="absolute inset-y-0 border-x-2 border-light-primaryText/50 dark:border-dark-primaryText/50"
                    style={{
                      left: pct(item.interval.low),
                      width: pct(item.interval.high - item.interval.low),
                    }}
                  />
                )}
              </div>
            )}

            <p className="mt-1 text-xs text-light-secondaryText dark:text-dark-secondaryText">
              {t('{{count}} answered', { count: item.n })}
              {item.interval
                ? ` · ${t('could be {{low}} to {{high}}', {
                    low: format(item.interval.low),
                    high: format(item.interval.high),
                  })}`
                : ''}
              {item.notApplicable
                ? ` · ${t('{{count}} said it did not apply', {
                    count: item.notApplicable,
                  })}`
                : ''}
            </p>
          </li>
        ))}
      </ol>
    );
  };

  return (
    <Card className={className}>
      <h2 className="text-base font-semibold text-light-primaryText dark:text-dark-primaryText">
        {t('Lowest scoring questions')}
      </h2>

      <div className="mt-3">{body()}</div>

      {!!items?.length && (
        <p className="mt-4 border-t border-light-alternate pt-3 text-xs text-light-secondaryText dark:border-dark-alternate dark:text-dark-secondaryText">
          {t(
            'Ranked by answer alone, from {{count}} responses. Where two bars ' +
              'overlap there is not enough here to separate them.',
            { count: respondentsInScope }
          )}{' '}
          {/* Permanent, because it is true of every pair of items in any
              questionnaire answered in one sitting, and because it is the
              thing a reader is most likely to forget. */}
          {t(
            'These questions were answered by the same people at the same ' +
              'sitting, so they will appear more related to each other than ' +
              'they are.'
          )}
          {unscoredQuestions > 0
            ? ` ${t(
                '{{count}} questions are not scored yet and are not ranked.',
                {
                  count: unscoredQuestions,
                }
              )}`
            : ''}
        </p>
      )}
    </Card>
  );
}

export default ItemRankingPanel;
